import PropTypes from 'prop-types';
import { trans, useSoyuzLocales } from '@spotahome/soyuz/client';
import { BILLS_INCLUDED_VALUES, isPlusPlan } from '@spotahome/ui-library';
import { DSIcon } from '@spotahome/ui-library/base';
import { getBillsIncluded } from '@spotahome/ui-library/src/utils/listing';

import ListingLabel from '../ListingLabels/ListingLabel';

import './ListingValuePropositionLabels.scss';

const MAX_LABELS = 3;

const MAIN_BILLS = [
  { id: 5000, transKey: 'search.filter.electricity' },
  { id: 5001, transKey: 'search.filter.water' },
  { id: 5002, transKey: 'search.filter.gas' },
  { id: 5003, transKey: 'search.filter.wifi' }
];

const getIncludedBillNames = (bills, locale) => {
  const names = MAIN_BILLS.filter(({ id }) => bills[id] === true).map(
    ({ transKey }) => trans(transKey)
  );
  const formatter = new Intl.ListFormat(locale, {
    style: 'long',
    type: 'conjunction'
  });
  return formatter.format(names);
};

const getLabels = ({ listing, hasNoDeposit, locale }) => {
  const {
    instantBooking = {},
    checked = false,
    bills = {},
    plan,
    bookingConditions
  } = listing;
  const { isEnabled: instantBookingEnabled = false } = instantBooking;
  const billsIncluded = getBillsIncluded(bills);
  const isBillsIncluded = billsIncluded === BILLS_INCLUDED_VALUES.all;
  const isSomeBillsIncluded = billsIncluded === BILLS_INCLUDED_VALUES.some;
  const showDepositProtection =
    !hasNoDeposit && (bookingConditions?.hasDepositProtection ?? true);
  const isPlus = isPlusPlan(plan);

  return [
    {
      id: 'checked',
      icon: 'check_circle_transparent',
      title: trans('property_show.book_now_card.checked'),
      description: trans('listing.vp.checked.description'),
      include: checked
    },
    {
      id: 'noDeposit',
      icon: 'savings',
      title: trans('listing.labels.noDeposit.title'),
      description: trans('listing.vp.no-deposit.description'),
      include: hasNoDeposit
    },
    {
      id: 'depositProtected',
      icon: 'lock_closed',
      title: trans('listing.labels.deposit-protected.title'),
      description: trans('listing.vp.deposit-protected.description'),
      include: showDepositProtection
    },
    {
      id: 'billsIncluded',
      icon: 'savings',
      title: trans('listing.labels.bills.all.title'),
      description: trans('listing.vp.bills-included.description'),
      include: isBillsIncluded
    },
    {
      id: 'someBillsIncluded',
      icon: 'savings',
      title: trans('listing.labels.bills.some.title'),
      description: trans('listing.vp.some-bills-included.description', {
        bills: getIncludedBillNames(bills, locale)
      }),
      include: isSomeBillsIncluded
    },
    {
      id: 'instantBooking',
      icon: 'electricity_bill',
      title: trans('property_show.book_now_card.instant_booking'),
      description: trans('listing.vp.instant-booking.description'),
      include: instantBookingEnabled
    },
    {
      id: 'verifiedLandlord',
      icon: 'verified',
      title: trans('listing.landlord_info.verified_landlord'),
      description: trans('listing.vp.verified-landlord.description'),
      include: true
    },
    {
      id: 'check24h',
      icon: 'mystery',
      title: trans('property_show.book_now_card.24h'),
      description: trans('listing.vp.check-24h.description'),
      include: true
    },
    {
      id: 'lastMinuteBackup',
      icon: 'support',
      title: trans('spotahome.guarantee.last.minute.backup'),
      description: trans('listing.vp.last-minute-backup.description'),
      include: true
    },
    {
      id: 'plus',
      icon: 'spoke',
      title: trans('Spotahome PLUS'),
      description: trans('listing.vp.plus.description'),
      include: isPlus
    }
  ]
    .filter(({ include }) => include)
    .slice(0, MAX_LABELS);
};

const ListingValuePropositionLabels = ({ listing, hasNoDeposit }) => {
  const { current: locale } = useSoyuzLocales();
  const labels = getLabels({ listing, hasNoDeposit, locale });

  return (
    <ul className="listing-vp-labels">
      {labels.map(({ id, icon, title, description }) => (
        <ListingLabel
          key={id}
          icon={<DSIcon name={icon} size="l" />}
          title={<span>{title}</span>}
        >
          {description}
        </ListingLabel>
      ))}
    </ul>
  );
};

ListingValuePropositionLabels.propTypes = {
  listing: PropTypes.shape({
    checked: PropTypes.bool,
    instantBooking: PropTypes.object,
    bills: PropTypes.object,
    plan: PropTypes.string,
    bookingConditions: PropTypes.shape({
      hasDepositProtection: PropTypes.bool
    })
  }).isRequired,
  hasNoDeposit: PropTypes.bool.isRequired
};

export default ListingValuePropositionLabels;
